import { useEffect, useState } from "react";
import { apiFetch, supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FileText, Plus, Trash2, ChevronUp, ChevronDown, Wand2, Copy } from "lucide-react";
import { WorkspaceCard, SectionHeader, FormField } from "@/components/design";

const FIELD_TYPES = [
  { value: "text", label: "Short text" },
  { value: "textarea", label: "Long text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "select", label: "Dropdown" },
  { value: "radio", label: "Radio" },
  { value: "checkbox", label: "Checkbox" },
];

const CLIENT_MAP_OPTIONS = [
  { value: "_none", label: "— None —" },
  { value: "client.first_name", label: "Client · First name" },
  { value: "client.last_name", label: "Client · Last name" },
  { value: "client.preferred_name", label: "Client · Preferred name" },
  { value: "client.date_of_birth", label: "Client · Date of birth" },
  { value: "client.phone", label: "Client · Phone" },
  { value: "client.email", label: "Client · Email" },
  { value: "client.street_address", label: "Client · Street address" },
  { value: "client.city", label: "Client · City" },
  { value: "client.state", label: "Client · State" },
  { value: "client.zip_code", label: "Client · ZIP" },
  { value: "client.emergency_contact_name", label: "Client · Emergency contact" },
  { value: "client.emergency_contact_phone", label: "Client · Emergency phone" },
  { value: "client.referral_source", label: "Client · Referral source" },
  { value: "client.notes", label: "Client · Notes" },
];

const emptyTpl = () => ({
  name: "", description: "", fields: [], is_intake: false, is_public: false, status: "draft",
  submit_button_label: "Submit", success_message: "Thank you — your response has been received.",
});

/**
 * FormPanel — three modes:
 *   • `mode === "new"`  → Form Builder (create a new template)
 *   • `mode === "edit"` → Form Builder editing an existing template (recordId = template id)
 *   • `mode === "view"` → Read-only viewer for a form_response (recordId = response id)
 *   • `mode === "fill"` → Fill-a-form mode for a specific client (templateId set)
 */
export default function FormPanel({ client, recordId, templateId, mode = "new", onSaved, onContext }) {
  const { currentOrgId } = useAuth();
  const [tpl, setTpl] = useState(emptyTpl());
  const [templateRow, setTemplateRow] = useState(null); // when editing existing template
  const [response, setResponse] = useState(null);
  const [answers, setAnswers] = useState({});
  const [fillTemplate, setFillTemplate] = useState(null); // selected template for fill mode
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load available templates once (for fill mode selection)
  useEffect(() => {
    if (!currentOrgId) return;
    supabase.from("form_templates").select("*")
      .eq("organization_id", currentOrgId).eq("status", "active").order("name")
      .then((r) => setAvailableTemplates(r.data || []));
  }, [currentOrgId]);

  // Hydrate from recordId based on mode
  useEffect(() => {
    if (mode === "view" && recordId) {
      setLoading(true);
      supabase.from("form_responses").select("*, form_templates(name, fields, status), clients(first_name,last_name)")
        .eq("id", recordId).limit(1).maybeSingle()
        .then(({ data }) => setResponse(data))
        .finally(() => setLoading(false));
      return;
    }
    if (mode === "edit" && recordId) {
      setLoading(true);
      supabase.from("form_templates").select("*").eq("id", recordId).limit(1).maybeSingle()
        .then(({ data }) => {
          if (!data) return;
          setTemplateRow(data);
          setTpl({
            name: data.name || "", description: data.description || "",
            fields: data.fields || [], is_intake: !!data.is_intake, is_public: !!data.is_public,
            status: data.status || "draft",
            submit_button_label: data.submit_button_label || "Submit",
            success_message: data.success_message || "Thank you — your response has been received.",
          });
        })
        .finally(() => setLoading(false));
      return;
    }
    if (mode === "new") {
      setTemplateRow(null); setTpl(emptyTpl()); return;
    }
    if (mode === "fill" && templateId) {
      const t = availableTemplates.find((x) => x.id === templateId);
      setFillTemplate(t || null);
    }
  }, [mode, recordId, templateId, availableTemplates]);

  // Prepopulate fill answers from client when template + client known
  useEffect(() => {
    if (mode !== "fill" || !fillTemplate || !client) return;
    const initial = {};
    (fillTemplate.fields || []).forEach((f) => {
      if (f.map_to?.startsWith("client.")) {
        const col = f.map_to.split(".")[1];
        if (client[col] != null) initial[f.id || f.label] = String(client[col]);
      }
    });
    setAnswers(initial);
  }, [fillTemplate?.id, client?.id, mode]);

  // -------- Builder actions --------
  const addField = () => setTpl({ ...tpl, fields: [...tpl.fields, {
    id: crypto.randomUUID(), type: "text", label: `Question ${tpl.fields.length + 1}`,
    required: false, options: [], placeholder: "", map_to: "",
  }] });
  const updField = (i, patch) => setTpl({ ...tpl, fields: tpl.fields.map((f, idx) => idx === i ? { ...f, ...patch } : f) });
  const rmField = (i) => setTpl({ ...tpl, fields: tpl.fields.filter((_, idx) => idx !== i) });
  const moveField = (i, dir) => {
    const j = i + dir; if (j < 0 || j >= tpl.fields.length) return;
    const next = [...tpl.fields]; [next[i], next[j]] = [next[j], next[i]];
    setTpl({ ...tpl, fields: next });
  };

  const saveTemplate = async (statusOverride) => {
    if (!tpl.name.trim()) return toast.error("Give the form a name");
    if (tpl.fields.length === 0) return toast.error("Add at least one field");
    const payload = { ...tpl, status: statusOverride || tpl.status };
    try {
      let r;
      if (templateRow) r = await apiFetch(`/forms/${templateRow.id}`, { method: "PATCH", body: JSON.stringify(payload) });
      else r = await apiFetch("/forms", { method: "POST", body: JSON.stringify({ ...payload, organization_id: currentOrgId }) });
      toast.success(templateRow ? "Form updated" : "Form created");
      onSaved && onSaved({ kind: "form", id: r?.id || templateRow?.id });
    } catch (e) { toast.error(e.message); }
  };

  // -------- Fill submit --------
  const submitFill = async () => {
    if (!fillTemplate) return toast.error("Pick a form template");
    if (!client) return toast.error("Pick a client first");
    const missing = (fillTemplate.fields || []).filter((f) => f.required && !String(answers[f.id || f.label] ?? "").trim()).map((f) => f.label);
    if (missing.length) return toast.error(`Fill required: ${missing.join(", ")}`);
    try {
      await apiFetch(`/forms/${fillTemplate.id}/submit`, {
        method: "POST",
        body: JSON.stringify({ form_template_id: fillTemplate.id, client_id: client.id, responses: answers }),
      });
      toast.success("Form submitted");
      onSaved && onSaved({ kind: "form", id: fillTemplate.id });
    } catch (e) { toast.error(e.message || "Submit failed"); }
  };

  const deleteTemplate = async () => {
    if (!templateRow) return;
    const warn = `Delete template "${templateRow.name}"? All its submitted responses (audit trail) will also be permanently deleted. This cannot be undone.`;
    if (!window.confirm(warn)) return;
    try {
      await apiFetch(`/forms/${templateRow.id}`, { method: "DELETE" });
      toast.success("Template deleted");
      onSaved && onSaved({ kind: "form", id: templateRow.id, deleted: true });
    } catch (e) { toast.error(e.message); }
  };
  const copyPublicLink = async () => {
    if (!templateRow?.slug) return toast.error("Save the template first (or enable Public sharing).");
    const url = `${window.location.origin}/f/${templateRow.slug}`;
    try { await navigator.clipboard.writeText(url); toast.success("Public link copied"); }
    catch { toast.error("Could not copy"); }
  };

  // -------- Context actions per mode --------
  useEffect(() => {
    if (mode === "view") {
      onContext && onContext({ client, editing: response, actions: [] });
      return;
    }
    if (mode === "fill") {
      onContext && onContext({
        client, editing: fillTemplate,
        actions: [
          { key: "submit", label: "Submit form", variant: "primary", testid: "form-submit", onClick: submitFill },
        ],
      });
      return;
    }
    // Builder (new or edit)
    onContext && onContext({
      client: null, editing: templateRow, status: tpl.status,
      actions: [
        { key: "draft", label: "Save as draft", variant: "outline", testid: "form-save-draft", onClick: () => saveTemplate("draft") },
        { key: "publish", label: templateRow ? "Save & publish" : "Create & publish", variant: "primary", testid: "form-publish", onClick: () => saveTemplate("active") },
        ...(templateRow && templateRow.is_public && templateRow.slug ? [
          { key: "copy-link", label: "Copy public link", icon: Copy, variant: "outline", testid: "form-copy-link", onClick: copyPublicLink },
        ] : []),
        ...(templateRow ? [
          { key: "delete", label: "Delete template", icon: Trash2, variant: "outline", testid: "form-delete", onClick: deleteTemplate },
        ] : []),
      ],
    });
    /* eslint-disable-next-line */
  }, [mode, tpl, templateRow, response, fillTemplate?.id, client?.id, answers]);

  if (loading) return <div className="text-center py-16 text-sm text-[#57534E]">Loading form…</div>;

  // -------- Render: view mode --------
  if (mode === "view" && response) {
    return (
      <div className="space-y-4" data-testid="workspace-form-view">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#57534E] font-semibold flex items-center gap-1.5"><FileText size={13} /> Completed Form</div>
          <h2 className="text-lg font-display font-semibold tracking-tight">{response.form_templates?.name || "Form"}</h2>
          {response.clients && <div className="text-xs text-[#57534E] mt-0.5">Submitted for {response.clients.first_name} {response.clients.last_name}</div>}
        </div>
        <WorkspaceCard>
          {(response.form_templates?.fields || []).length > 0
            ? (response.form_templates.fields || []).map((f, i) => {
                const val = response.responses?.[f.id || f.label];
                return (
                  <div key={f.id || i} className="border-b border-[#E8E2D9] pb-3 last:border-0">
                    <div className="text-[11px] text-[#A8A29E] uppercase tracking-widest">{f.label}</div>
                    <div className="text-sm text-[#1C1917] mt-0.5 whitespace-pre-wrap">
                      {(val === "" || val == null) ? <span className="text-[#A8A29E]">—</span> : String(val)}
                    </div>
                  </div>
                );
              })
            : Object.entries(response.responses || {}).map(([k, v]) => (
                <div key={k} className="border-b border-[#E8E2D9] pb-3 last:border-0">
                  <div className="text-[11px] text-[#A8A29E] uppercase tracking-widest">{k}</div>
                  <div className="text-sm text-[#1C1917] mt-0.5 whitespace-pre-wrap">{String(v ?? "—")}</div>
                </div>
              ))}
        </WorkspaceCard>
      </div>
    );
  }

  // -------- Render: fill mode --------
  if (mode === "fill" && fillTemplate) {
    return (
      <div className="space-y-4" data-testid="workspace-form-fill">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#57534E] font-semibold flex items-center gap-1.5"><FileText size={13} /> Fill Form</div>
          <h2 className="text-lg font-display font-semibold tracking-tight">{fillTemplate.name}</h2>
          {client && <div className="text-xs text-[#57534E] mt-0.5">for {client.first_name} {client.last_name}</div>}
        </div>
        <WorkspaceCard className="space-y-5">
          {fillTemplate.description && <p className="text-sm text-[#57534E]">{fillTemplate.description}</p>}
          {(fillTemplate.fields || []).map((f, i) => (
            <FillFieldInput key={f.id || i} field={f}
              value={answers[f.id || f.label] ?? ""}
              onChange={(v) => setAnswers({ ...answers, [f.id || f.label]: v })} />
          ))}
          {(fillTemplate.fields || []).length === 0 && <p className="text-xs text-[#A8A29E]">This template has no fields configured.</p>}
        </WorkspaceCard>
      </div>
    );
  }

  // -------- Render: builder (new / edit) --------
  return (
    <div className="space-y-4" data-testid="workspace-form-builder">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-[#57534E] font-semibold flex items-center gap-1.5"><Wand2 size={13} /> Form Builder</div>
        <h2 className="text-lg font-display font-semibold tracking-tight">{templateRow ? tpl.name || "Edit form" : "New form"}</h2>
      </div>

      <WorkspaceCard>
        <SectionHeader title="Form details" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <FormField label="Form name" required>
            <Input data-testid="form-tpl-name" value={tpl.name} onChange={(e) => setTpl({ ...tpl, name: e.target.value })} className="h-10" />
          </FormField>
          <FormField label="Status">
            <Select value={tpl.status} onValueChange={(v) => setTpl({ ...tpl, status: v })}>
              <SelectTrigger className="h-10" data-testid="form-tpl-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </FormField>
          <FormField label="Description" wide>
            <Textarea rows={2} data-testid="form-tpl-desc" value={tpl.description} onChange={(e) => setTpl({ ...tpl, description: e.target.value })} />
          </FormField>
          <FormField label="Submit button label">
            <Input value={tpl.submit_button_label} onChange={(e) => setTpl({ ...tpl, submit_button_label: e.target.value })} className="h-10" />
          </FormField>
          <FormField label="Success message">
            <Input value={tpl.success_message} onChange={(e) => setTpl({ ...tpl, success_message: e.target.value })} className="h-10" />
          </FormField>
          <div className="md:col-span-2 flex flex-wrap gap-5 pt-1">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={tpl.is_intake} onChange={(e) => setTpl({ ...tpl, is_intake: e.target.checked })} className="w-4 h-4 accent-[#E8712C]" data-testid="form-tpl-intake" />
              Use as an intake form
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={tpl.is_public} onChange={(e) => setTpl({ ...tpl, is_public: e.target.checked })} className="w-4 h-4 accent-[#E8712C]" data-testid="form-tpl-public" />
              Publicly shareable via link
            </label>
          </div>
        </div>
      </WorkspaceCard>

      <WorkspaceCard>
        <SectionHeader
          title="Fields"
          hint="Reorder with the arrows · toggle required · map fields to client record"
          action={
            <button type="button" onClick={addField} data-testid="form-add-field"
              className="rounded-full border border-[#E8E2D9] px-3 py-1.5 text-xs font-medium hover:bg-[#FDE9D8] flex items-center gap-1.5">
              <Plus size={12} /> Add field
            </button>
          }
        />
        {tpl.fields.length === 0 ? (
          <div className="text-center py-8 text-xs text-[#57534E] bg-[#FAF8F5] rounded-xl border border-dashed border-[#E8E2D9]">
            No fields yet. Click <b>Add field</b> to build your first question.
          </div>
        ) : (
          <div className="space-y-2.5">
            {tpl.fields.map((f, i) => (
              <div key={f.id || i} className="bg-[#FAF8F5] rounded-xl border border-[#E8E2D9] p-3 space-y-2.5" data-testid={`form-field-row-${i}`}>
                <div className="flex items-center gap-2">
                  <div className="flex flex-col">
                    <button type="button" onClick={() => moveField(i, -1)} className="text-[#A8A29E] hover:text-[#1C1917]" title="Move up" disabled={i === 0}><ChevronUp size={13} /></button>
                    <button type="button" onClick={() => moveField(i, 1)} className="text-[#A8A29E] hover:text-[#1C1917]" title="Move down" disabled={i === tpl.fields.length - 1}><ChevronDown size={13} /></button>
                  </div>
                  <Input value={f.label} onChange={(e) => updField(i, { label: e.target.value })} placeholder="Question label" className="flex-1 h-10 bg-white" data-testid={`form-field-label-${i}`} />
                  <Select value={f.type} onValueChange={(v) => updField(i, { type: v })}>
                    <SelectTrigger className="w-36 h-10 bg-white" data-testid={`form-field-type-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{FIELD_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <button type="button" onClick={() => rmField(i)} className="text-[#B02222] hover:bg-[#FDECEC] rounded-full p-1.5" data-testid={`form-remove-field-${i}`} title="Remove"><Trash2 size={13} /></button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <Input value={f.placeholder || ""} onChange={(e) => updField(i, { placeholder: e.target.value })} placeholder="Placeholder / help text" className="text-sm h-10 bg-white" />
                  <Select value={f.map_to || "_none"} onValueChange={(v) => updField(i, { map_to: v === "_none" ? "" : v })}>
                    <SelectTrigger className="text-sm h-10 bg-white" data-testid={`form-field-mapto-${i}`}><SelectValue placeholder="Auto-populate to client record" /></SelectTrigger>
                    <SelectContent>{CLIENT_MAP_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {(f.type === "select" || f.type === "radio") && (
                  <div>
                    <Label htmlFor={`opts-${i}`} className="text-xs text-[#57534E]">Options (one per line)</Label>
                    <Textarea id={`opts-${i}`} rows={3} value={(f.options || []).join("\n")}
                      onChange={(e) => updField(i, { options: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
                      className="mt-1 text-sm bg-white" data-testid={`form-field-options-${i}`} />
                  </div>
                )}
                <label className="flex items-center gap-2 text-xs cursor-pointer text-[#57534E]">
                  <input type="checkbox" checked={!!f.required} onChange={(e) => updField(i, { required: e.target.checked })} className="w-3.5 h-3.5 accent-[#E8712C]" data-testid={`form-field-required-${i}`} />
                  Required
                </label>
              </div>
            ))}
          </div>
        )}
      </WorkspaceCard>
    </div>
  );
}

// Local label — only used inside the builder for tiny option labels.
function Label({ htmlFor, className = "", children }) {
  return <label htmlFor={htmlFor} className={`text-sm font-medium text-[#1C1917] ${className}`}>{children}</label>;
}

function FillFieldInput({ field, value, onChange }) {
  const key = field.id || field.label;
  const common = { id: key, "data-testid": `form-fill-${key}`, value, onChange: (e) => onChange(e.target.value), placeholder: field.placeholder || "", className: "h-10" };
  return (
    <FormField label={field.label} required={field.required} htmlFor={key}>
      {field.type === "textarea" ? (
        <Textarea rows={3} {...common} className="" />
      ) : field.type === "select" ? (
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger className="h-10"><SelectValue placeholder={field.placeholder || "Select…"} /></SelectTrigger>
          <SelectContent>{(field.options || []).filter((o) => o && String(o).trim()).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
      ) : field.type === "checkbox" ? (
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={value === true || value === "true"} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-[#E8712C]" />
          {field.label}
        </label>
      ) : field.type === "radio" ? (
        <div className="space-y-1.5">
          {(field.options || []).map((o) => (
            <label key={o} className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="radio" name={key} value={o} checked={value === o} onChange={() => onChange(o)} className="accent-[#E8712C]" />{o}
            </label>
          ))}
        </div>
      ) : (
        <Input type={{ number: "number", date: "date", email: "email", phone: "tel" }[field.type] || "text"} {...common} />
      )}
    </FormField>
  );
}
